import BigNumber from 'bignumber.js'
import erc20ABI from '~/config/abi/erc20.json'
import masterchefABI from '~/config/abi/masterchef.json'
import rewarderABI from '~/config/abi/rewarder.json'
import multicall from '~/utils/multicall'
import { getAddress, getFarmZapAddress, getMasterChefAddress } from '~/utils/addressHelpers'
import { FarmConfig } from '~/config/constants/types'

export const fetchFarmUserAllowances = async (chainId: number, account: string, farmsToFetch: FarmConfig[]) => {
  const masterChefAddress = getMasterChefAddress(chainId)

  const calls = farmsToFetch.map((farm) => {
    const lpContractAddress = getAddress(chainId, farm.lpAddresses)
    return { address: lpContractAddress, name: 'allowance', params: [account, masterChefAddress] }
  })

  const rawLpAllowances = await multicall(chainId, erc20ABI, calls)

  const parsedLpAllowances = rawLpAllowances.map((lpBalance) => {
    return new BigNumber(lpBalance).toJSON()
  })
  return parsedLpAllowances
}

export const fetchZapFarmUserAllowances = async (chainId: number, account: string, farmsToFetch: FarmConfig[]) => {
  const farmZapAddress = getFarmZapAddress(chainId)

  const calls = farmsToFetch.map((farm) => {
    const lpContractAddress = getAddress(chainId, farm.lpAddresses)
    return { address: lpContractAddress, name: 'allowance', params: [account, farmZapAddress] }
  })

  // const rawLpAllowances = await multicall(lpTokenABI, calls)
  const rawLpAllowances = await multicall(chainId, erc20ABI, calls)

  const parsedLpAllowances = rawLpAllowances.map((lpBalance) => {
    return new BigNumber(lpBalance).toJSON()
  })
  return parsedLpAllowances
}

export const fetchFarmUserTokenBalances = async (chainId: number, account: string, farmsToFetch: FarmConfig[]) => {
  const calls = farmsToFetch.map((farm) => {
    const lpContractAddress = getAddress(chainId, farm.lpAddresses)
    return {
      address: lpContractAddress,
      name: 'balanceOf',
      params: [account],
    }
  })

  const rawTokenBalances = await multicall(chainId, erc20ABI, calls)
  const parsedTokenBalances = rawTokenBalances.map((tokenBalance) => {
    return new BigNumber(tokenBalance).toJSON()
  })
  return parsedTokenBalances
}

export const fetchFarmUserStakedBalances = async (chainId: number, account: string, farmsToFetch: FarmConfig[]) => {
  const masterChefAddress = getMasterChefAddress(chainId)

  const calls = farmsToFetch.map((farm) => {
    return {
      address: masterChefAddress,
      name: 'userInfo',
      params: [farm.pid, account],
    }
  })

  const rawStakedBalances = await multicall(chainId, masterchefABI, calls)
  const parsedStakedBalances = rawStakedBalances.map((stakedBalance) => {
    return new BigNumber(stakedBalance[0]._hex).toJSON()
  })
  return parsedStakedBalances
}

export const fetchFarmUserEarnings = async (chainId: number, account: string, farmsToFetch: FarmConfig[]) => {
  const masterChefAddress = getMasterChefAddress(chainId)

  const calls = farmsToFetch.map((farm) => {
    return {
      address: masterChefAddress,
      name: 'pendingRewards',
      params: [farm.pid, account],
    }
  })

  const rawEarnings = await multicall(chainId, masterchefABI, calls)
  const parsedEarnings = rawEarnings.map((earnings) => {
    return new BigNumber(earnings).toJSON()
  })
  return parsedEarnings
}



const fetchSingleFarmUserDualEarnings = async (chainId: number, account: string, farm: FarmConfig) => {
  const calls = [
    {
      address: getAddress(chainId, farm.rewarder),
      name: 'pendingTokens',
      params: [farm.pid, account,0], // third param is ignored in contract
    },
  ]

  const rawEarnings = await multicall(chainId, rewarderABI, calls)

  const parsedEarnings = rawEarnings.map(({ rewardAmounts, rewardTokens }) => {
    return rewardAmounts.map((amount, index) => {
      return {
        rewardAmount: new BigNumber(amount._hex).toJSON(),
        rewardToken: rewardTokens[index].toString(),
      }
    })
  })
  return parsedEarnings[0]
}


export const fetchFarmUserDualEarnings = async (chainId: number, account: string, farmsConfig: FarmConfig[]) => {
  const dualEarnings = farmsConfig.map( async (farm) => {
    if (farm.rewarder) {
      return fetchSingleFarmUserDualEarnings(chainId, account, farm);
    }
    return Promise.resolve(undefined)
  })

  return Promise.all(dualEarnings);
}
